import { datadogLogs as _datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { useEffect, useState } from 'react';

import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { isSelfHosted } from '@/utils/is-self-hosted';

import {
  datadogAppTokenRUM,
  datadogClientToken,
  datadogClientTokenRUM,
  datadogEnv,
  datadogSite,
  datadogVersion,
} from '../constants';

interface ErrorRule {
  messageIncludes?: string;
  fileIncludes?: string;
}

const ERROR_EXCLUSION_RULES: ErrorRule[] = [
  {
    // generic fetch error
    messageIncludes: 'Failed to fetch',
  },

  {
    // Timeout error connecting to local langgraph
    messageIncludes: 'signal timed out',
  },
  {
    // Another studio related error
    messageIncludes: 'when attempting to fetch resource.',
  },
  {
    // Timeout error connecting to local langgraph
    messageIncludes: 'operation timed out',
  },
  {
    // Auth error
    messageIncludes: 'Forbidden',
  },
  {
    // User aborted request
    messageIncludes: 'The user aborted a request.',
  },

  {
    fileIncludes: 'async_caller',
  },
  {
    messageIncludes:
      'ResizeObserver loop completed with undelivered notifications',
  },
  {
    messageIncludes: 'net::ERR_BLOCKED_BY_CLIENT',
  },
];

function shouldExcludeError(error: {
  type?: string;
  message: string;
  file?: string;
  stack?: string;
}) {
  return ERROR_EXCLUSION_RULES.some(
    (rule) =>
      (rule.messageIncludes &&
        error.message
          .toLowerCase()
          .includes(rule.messageIncludes.toLowerCase())) ||
      (rule.messageIncludes &&
        error.stack
          ?.toLowerCase()
          .includes(rule.messageIncludes.toLowerCase())) ||
      (rule.fileIncludes &&
        error.file?.toLowerCase().includes(rule.fileIncludes.toLowerCase()))
  );
}

export const useInitializeDatadog = () => {
  const orgConfig = useOrgConfig(OrgConfigs.datadog_rum_session_sample_rate);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [datadogLogs, setDatadogLogs] = useState<
    typeof _datadogLogs | undefined
  >(undefined);
  useEffect(() => {
    if (isSelfHosted) return;
    if (
      datadogAppTokenRUM &&
      datadogClientTokenRUM &&
      datadogSite &&
      !orgConfig.isLoading &&
      !hasInitialized &&
      orgConfig.value != null
    ) {
      const sessionSampleRate =
        typeof orgConfig.value === 'number' ? orgConfig.value : 20;
      datadogRum.init({
        applicationId: datadogAppTokenRUM,
        clientToken: datadogClientTokenRUM,
        site: datadogSite,
        service: 'frontend',
        env: datadogEnv ?? 'dev',
        version: datadogVersion,
        sessionSampleRate: 100,
        sessionReplaySampleRate: sessionSampleRate,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend(event) {
          if (event.type === 'error') {
            return !shouldExcludeError(event.error);
          }
          return true;
        },
      });
      setHasInitialized(true);
    }

    if (datadogClientToken && datadogSite && !isSelfHosted && !datadogLogs) {
      _datadogLogs.init({
        clientToken: datadogClientToken,
        site: datadogSite,
        env: datadogEnv ?? 'dev',
        version: datadogVersion,
        service: 'frontend',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        beforeSend(event) {
          if (event.type === 'error') {
            return !shouldExcludeError({
              message: event.message,
            });
          }
          return true;
        },
      });
      setDatadogLogs(_datadogLogs);
    }
  }, [orgConfig.value, orgConfig.isLoading, hasInitialized, datadogLogs]);

  return { datadogLogs };
};
